import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Plus,
  Loader2,
  Search,
  Filter,
  MoreVertical,
  Eye,
  Pencil,
  Trash2,
  CheckSquare,
  X
} from 'lucide-react';
import { Checkbox } from '../components/ui/checkbox';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const WORK_ITEM_TYPES = ['Epic', 'Feature', 'User Story', 'Task', 'Bug'];
const STATES = ['New', 'Active', 'In Progress', 'Resolved', 'Done', 'Closed'];

export default function WorkItems() {
  const { getAuthHeader, user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const isReader = user?.role === 'reader';

  const [workItems, setWorkItems] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  // Edit state
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [updating, setUpdating] = useState(false);

  // Delete state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Bulk selection state
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [bulkEditDialogOpen, setBulkEditDialogOpen] = useState(false);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ state: '', assigned_to: '', iteration_path: '', tags: '' });
  const [bulkProcessing, setBulkProcessing] = useState(false);

  const [filters, setFilters] = useState({
    project_id: searchParams.get('project') || '',
    work_item_type: '',
    search: '',
    tag: '',
    state: ''
  });

  const [newItem, setNewItem] = useState({
    title: '',
    description: '',
    work_item_type: 'Task',
    state: 'New',
    project_id: searchParams.get('project') || '',
    parent_id: '',
    completed_hours: 0,
    tags: '',
    iteration_path: ''
  });

  // Iterations for create/edit forms
  const [createIterations, setCreateIterations] = useState([]);
  const [editIterations, setEditIterations] = useState([]);

  useEffect(() => {
    fetchProjects();
    fetchWorkItems();
    setSelectedIds(new Set());
  }, [filters.project_id, filters.work_item_type]);

  // Auto-open the creation dialog when navigating via /work-items/new
  useEffect(() => {
    if (location.pathname === '/work-items/new' && !isReader) {
      setDialogOpen(true);
      navigate('/work-items', { replace: true });
    }
  }, [location.pathname, isReader]);

  const fetchProjects = async () => {
    try {
      // Fetch projects assigned to the current user
      const response = await axios.get(`${API_URL}/api/users/me/projects`, {
        headers: getAuthHeader()
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Failed to fetch projects:', error);
      toast.error('Erreur lors du chargement des projets');
    }
  };

  const fetchIterations = async (projectName) => {
    try {
      const response = await axios.get(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(projectName)}/iterations`,
        { headers: getAuthHeader() }
      );
      return response.data.iterations || [];
    } catch {
      return [];
    }
  };

  const fetchWorkItems = async () => {
    try {
      const params = new URLSearchParams();
      if (filters.project_id) params.append('project_id', filters.project_id);
      if (filters.work_item_type) params.append('work_item_type', filters.work_item_type);

      // Fetch only Azure DevOps work items
      const response = await axios.get(`${API_URL}/api/work-items/azure?${params}`, {
        headers: getAuthHeader()
      });

      // Extract and mark as Azure work items
      const azureWorkItems = (response.data.work_items || []).map(item => ({
        ...item,
        source: 'azure',
        is_local: false
      }));

      setWorkItems(azureWorkItems);
    } catch (error) {
      console.error('Failed to fetch Azure work items:', error);
      if (error.response?.status === 400) {
        toast.error('Azure DevOps non configuré');
      } else {
        toast.error('Erreur lors du chargement des tâches');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCreateItem = async (e) => {
    e.preventDefault();
    if (!newItem.project_id) {
      toast.error('Veuillez sélectionner un projet');
      return;
    }

    setCreating(true);
    try {
      // Find the project name from project_id
      const selectedProject = projects.find(p => p.id === newItem.project_id);
      if (!selectedProject) {
        toast.error('Projet introuvable');
        return;
      }

      // Prepare data for Azure DevOps API
      const azureData = {
        title: newItem.title,
        work_item_type: newItem.work_item_type,
        description: newItem.description || null,
        state: newItem.state || 'New',
        parent_id: newItem.parent_id ? parseInt(newItem.parent_id) : null,
        completed_hours: newItem.completed_hours || 0,
        tags: newItem.tags || null,
        iteration_path: newItem.iteration_path || null,
      };

      const response = await axios.post(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(selectedProject.name)}/work-items`,
        azureData,
        { headers: getAuthHeader() }
      );

      toast.success('Work item créé dans Azure DevOps');
      setDialogOpen(false);
      setNewItem({
        title: '',
        description: '',
        work_item_type: 'Task',
        state: 'New',
        project_id: filters.project_id || '',
        parent_id: '',
        completed_hours: 0,
        tags: '',
        iteration_path: ''
      });
      fetchWorkItems();
    } catch (error) {
      console.error('Error creating Azure work item:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la création dans Azure DevOps');
    } finally {
      setCreating(false);
    }
  };

  const handleEditItem = async (e) => {
    e.preventDefault();
    if (!editItem) return;
    setUpdating(true);
    try {
      await axios.patch(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(editItem.project_name)}/work-items/${editItem.azure_id}`,
        {
          title: editItem.title,
          description: editItem.description || null,
          state: editItem.state,
          assigned_to: editItem.assigned_to || null,
          area_path: editItem.area_path || null,
          iteration_path: editItem.iteration_path || null,
          completed_hours: editItem.completed_hours || null,
          tags: editItem.tags || null,
        },
        { headers: getAuthHeader() }
      );
      toast.success(`Work item #${editItem.azure_id} mis à jour`);
      setEditDialogOpen(false);
      setEditItem(null);
      fetchWorkItems();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la mise à jour';
      toast.error(msg);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteItem = async () => {
    if (!deleteItem) return;
    setDeleting(true);
    try {
      await axios.delete(
        `${API_URL}/api/azure-devops/projects/${encodeURIComponent(deleteItem.project_name)}/work-items/${deleteItem.azure_id}`,
        { headers: getAuthHeader() }
      );
      toast.success(`Work item #${deleteItem.azure_id} supprimé`);
      setDeleteDialogOpen(false);
      setDeleteItem(null);
      fetchWorkItems();
    } catch (error) {
      const msg = error.response?.data?.detail || 'Erreur lors de la suppression';
      toast.error(msg);
    } finally {
      setDeleting(false);
    }
  };

  const openEditDialog = async (item) => {
    setEditItem({
      azure_id: item.azure_id || item.id,
      project_name: item.project_name,
      work_item_type: item.work_item_type || 'Task',
      title: item.title || '',
      description: item.description || '',
      state: item.state || 'New',
      assigned_to: item.assigned_to || '',
      area_path: item.area_path || '',
      iteration_path: item.iteration_path || '',
      completed_hours: item.completed_hours || 0,
      tags: (item.tags || []).join('; '),
    });
    setEditDialogOpen(true);
    if (item.project_name) {
      const iters = await fetchIterations(item.project_name);
      setEditIterations(iters);
    }
  };

  const openDeleteDialog = (item) => {
    setDeleteItem({
      ...item,
      azure_id: item.azure_id || item.id,
    });
    setDeleteDialogOpen(true);
  };

  const toggleSelectItem = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === filteredItems.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(filteredItems.map(i => i.id)));
    }
  };

  const handleBulkEdit = async () => {
    const itemsToEdit = filteredItems.filter(i => selectedIds.has(i.id));
    if (itemsToEdit.length === 0) return;
    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    for (const item of itemsToEdit) {
      try {
        const payload = {};
        if (bulkEditData.state) payload.state = bulkEditData.state;
        if (bulkEditData.assigned_to) payload.assigned_to = bulkEditData.assigned_to;
        if (bulkEditData.tags) payload.tags = bulkEditData.tags;
        if (Object.keys(payload).length === 0) break;
        await axios.patch(
          `${API_URL}/api/azure-devops/projects/${encodeURIComponent(item.project_name)}/work-items/${item.azure_id || item.id}`,
          payload,
          { headers: getAuthHeader() }
        );
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setBulkProcessing(false);
    setBulkEditDialogOpen(false);
    setBulkEditData({ state: '', assigned_to: '', iteration_path: '', tags: '' });
    setSelectedIds(new Set());
    if (successCount > 0) toast.success(`${successCount} tâche(s) mise(s) à jour`);
    if (errorCount > 0) toast.error(`${errorCount} tâche(s) n'ont pas pu être mises à jour`);
    fetchWorkItems();
  };

  const handleBulkDelete = async () => {
    const itemsToDelete = filteredItems.filter(i => selectedIds.has(i.id));
    if (itemsToDelete.length === 0) return;
    setBulkProcessing(true);
    let successCount = 0;
    let errorCount = 0;
    for (const item of itemsToDelete) {
      try {
        await axios.delete(
          `${API_URL}/api/azure-devops/projects/${encodeURIComponent(item.project_name)}/work-items/${item.azure_id || item.id}`,
          { headers: getAuthHeader() }
        );
        successCount++;
      } catch {
        errorCount++;
      }
    }
    setBulkProcessing(false);
    setBulkDeleteDialogOpen(false);
    setSelectedIds(new Set());
    if (successCount > 0) toast.success(`${successCount} tâche(s) supprimée(s)`);
    if (errorCount > 0) toast.error(`${errorCount} tâche(s) n'ont pas pu être supprimées`);
    fetchWorkItems();
  };

  const getWorkItemTypeClass = (type) => {
    const classes = {
      'Epic': 'work-item-epic',
      'Feature': 'work-item-feature',
      'User Story': 'work-item-userstory',
      'Task': 'work-item-task',
      'Bug': 'work-item-bug'
    };
    return classes[type] || '';
  };

  const getStateClass = (state) => {
    if (['Done', 'Closed', 'Completed'].includes(state)) return 'state-done';
    if (['Active', 'In Progress'].includes(state)) return 'state-active';
    return 'state-new';
  };

  // Extract unique tags from all work items
  const allTags = [...new Set(workItems.flatMap(item => item.tags || []))].sort();

  const filteredItems = workItems.filter(item => {
    if (filters.search && !item.title.toLowerCase().includes(filters.search.toLowerCase())) return false;
    if (filters.tag && !(item.tags || []).includes(filters.tag)) return false;
    if (filters.state && item.state !== filters.state) return false;
    return true;
  });

  const getValidParentTypes = (type) => {
    switch (type) {
      case 'Task':
      case 'Bug': return ['User Story'];
      case 'User Story': return ['Feature'];
      case 'Feature': return ['Epic'];
      case 'Epic': return [];
      default: return [];
    }
  };

  const getTypeLabel = (type) => {
    switch (type) {
      case 'Epic': return 'un Epic';
      case 'Feature': return 'une Feature';
      case 'User Story': return 'une User Story';
      case 'Bug': return 'un Bug';
      default: return 'une Tâche';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="work-items-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-heading font-extrabold">Tâches</h1>
          <p className="text-muted-foreground mt-1">
            Gérez et visualisez vos tâches Azure DevOps
          </p>
        </div>

        {!isReader && <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button className="rounded-sm" data-testid="new-work-item-btn">
              <Plus className="h-4 w-4 mr-2" />
              Nouvelle Tâche
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl w-full">
            <DialogHeader>
              <DialogTitle>Créer {getTypeLabel(newItem.work_item_type)} Azure DevOps</DialogTitle>
              <DialogDescription>
                L'élément sera créé directement dans Azure DevOps
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateItem} className="w-full min-w-0">
              <div className="grid grid-cols-2 gap-x-4 gap-y-3 py-4 w-full min-w-0">

                {/* Type + Titre */}
                <div className="space-y-1.5">
                  <Label>Type *</Label>
                  <Select
                    value={newItem.work_item_type}
                    onValueChange={(v) => setNewItem({ ...newItem, work_item_type: v, parent_id: '' })}
                  >
                    <SelectTrigger data-testid="work-item-type-select">
                      <SelectValue placeholder="Sélectionner un type" />
                    </SelectTrigger>
                    <SelectContent>
                      {WORK_ITEM_TYPES.map(type => (
                        <SelectItem key={type} value={type}>{type}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="title">Titre *</Label>
                  <Input
                    id="title"
                    value={newItem.title}
                    onChange={(e) => setNewItem({ ...newItem, title: e.target.value })}
                    placeholder="Titre de la tâche"
                    required
                    data-testid="work-item-title-input"
                  />
                </div>

                {/* Projet + Sprint */}
                <div className="space-y-1.5">
                  <Label>Projet *</Label>
                  <Select
                    value={newItem.project_id}
                    onValueChange={async (v) => {
                      const proj = projects.find(p => p.id === v);
                      setNewItem({ ...newItem, project_id: v, iteration_path: proj ? proj.name : '', parent_id: '' });
                      if (proj) {
                        const iters = await fetchIterations(proj.name);
                        setCreateIterations(iters);
                      } else {
                        setCreateIterations([]);
                      }
                    }}
                  >
                    <SelectTrigger data-testid="work-item-project-select">
                      <SelectValue placeholder="Sélectionner un projet" />
                    </SelectTrigger>
                    <SelectContent>
                      {projects.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1.5">
                  <Label>Sprint</Label>
                  <Select
                    disabled={!newItem.project_id}
                    value={
                      !newItem.iteration_path ||
                      newItem.iteration_path === (projects.find(p => p.id === newItem.project_id)?.name || '')
                        ? "project_root"
                        : newItem.iteration_path
                    }
                    onValueChange={(v) => setNewItem({
                      ...newItem,
                      iteration_path: v === "project_root"
                        ? (projects.find(p => p.id === newItem.project_id)?.name || '')
                        : v
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Aucun sprint" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_root">
                        {projects.find(p => p.id === newItem.project_id)?.name || 'Projet'} (sans sprint)
                      </SelectItem>
                      {createIterations.map(it => (
                        <SelectItem key={it.id} value={it.path}>{it.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Parent (pleine largeur, conditionnel) */}
                {newItem.work_item_type !== 'Epic' && (() => {
                  const selectedProjectName = projects.find(p => p.id === newItem.project_id)?.name;
                  const validParents = workItems.filter(w =>
                    w.azure_id &&
                    getValidParentTypes(newItem.work_item_type).includes(w.work_item_type) &&
                    w.project_name === selectedProjectName
                  );
                  return (
                    <div className="col-span-2 space-y-1.5">
                      <Label>
                        Parent ({getValidParentTypes(newItem.work_item_type).join(' ou ')} uniquement)
                      </Label>
                      <Select
                        value={newItem.parent_id || "none"}
                        onValueChange={(v) => setNewItem({ ...newItem, parent_id: v === "none" ? "" : v })}
                      >
                        <SelectTrigger data-testid="work-item-parent-select">
                          <SelectValue placeholder="Aucun parent" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Aucun parent</SelectItem>
                          {validParents.map(p => (
                            <SelectItem key={p.id} value={String(p.azure_id)}>
                              [{p.work_item_type}] {p.title}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {newItem.project_id && validParents.length === 0 && (
                        <p className="text-xs text-muted-foreground">
                          Aucun {getValidParentTypes(newItem.work_item_type).join('/')} disponible dans ce projet
                        </p>
                      )}
                    </div>
                  );
                })()}

                {/* Heures + Tags */}
                {newItem.work_item_type === 'Task' && (
                <div className="space-y-1.5">
                  <Label htmlFor="completed_hours">Temps complété (h)</Label>
                  <Input
                    id="completed_hours"
                    type="number"
                    min="0"
                    step="0.5"
                    value={newItem.completed_hours || 0}
                    onChange={(e) => setNewItem({ ...newItem, completed_hours: parseFloat(e.target.value) || 0 })}
                    data-testid="work-item-completed-hours-input"
                    placeholder="0"
                  />
                </div>
                )}

                <div className="space-y-1.5">
                  <Label htmlFor="tags">Tags</Label>
                  <Input
                    id="tags"
                    value={newItem.tags}
                    onChange={(e) => setNewItem({ ...newItem, tags: e.target.value })}
                    placeholder="Tag1; Tag2"
                    data-testid="work-item-tags-input"
                  />
                </div>

                {/* Description (pleine largeur) */}
                <div className="col-span-2 space-y-1.5">
                  <Label htmlFor="description">Description</Label>
                  <Textarea
                    id="description"
                    value={newItem.description}
                    onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Description de la tâche..."
                    rows={3}
                    data-testid="work-item-description-input"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={creating} data-testid="create-work-item-btn">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Créer'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Rechercher..."
                className="pl-10"
                value={filters.search}
                onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                data-testid="work-item-search-input"
              />
            </div>
            <Select
              value={filters.project_id || "all"}
              onValueChange={(v) => setFilters({ ...filters, project_id: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-[200px]" data-testid="filter-project-select">
                <SelectValue placeholder="Tous les projets" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les projets</SelectItem>
                {projects.map(p => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.state || "all"}
              onValueChange={(v) => setFilters({ ...filters, state: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-[160px]" data-testid="filter-state-select">
                <SelectValue placeholder="Tous les états" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les états</SelectItem>
                {STATES.map(s => (
                  <SelectItem key={s} value={s}>{s}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.work_item_type || "all"}
              onValueChange={(v) => setFilters({ ...filters, work_item_type: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-[150px]" data-testid="filter-type-select">
                <SelectValue placeholder="Tous types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous types</SelectItem>
                {WORK_ITEM_TYPES.map(type => (
                  <SelectItem key={type} value={type}>{type}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.tag || "all"}
              onValueChange={(v) => setFilters({ ...filters, tag: v === "all" ? "" : v })}
            >
              <SelectTrigger className="w-[180px]" data-testid="filter-tag-select">
                <SelectValue placeholder="Tous les tags" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les tags</SelectItem>
                {allTags.map(tag => (
                  <SelectItem key={tag} value={tag}>{tag}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk action bar */}
      {selectedIds.size > 0 && !isReader && (
        <div className="flex items-center gap-3 px-4 py-3 bg-primary/10 border border-primary/30 rounded-sm">
          <CheckSquare className="h-4 w-4 text-primary shrink-0" />
          <span className="text-sm font-medium flex-1">
            {selectedIds.size} tâche{selectedIds.size > 1 ? 's' : ''} sélectionnée{selectedIds.size > 1 ? 's' : ''}
          </span>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm"
            onClick={() => {
              setBulkEditData({ state: '', assigned_to: '', iteration_path: '', tags: '' });
              setBulkEditDialogOpen(true);
            }}
          >
            <Pencil className="h-3.5 w-3.5 mr-1.5" />
            Modifier
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="rounded-sm text-destructive border-destructive/40 hover:bg-destructive/10"
            onClick={() => setBulkDeleteDialogOpen(true)}
          >
            <Trash2 className="h-3.5 w-3.5 mr-1.5" />
            Supprimer
          </Button>
          <Button
            size="sm"
            variant="ghost"
            className="rounded-sm"
            onClick={() => setSelectedIds(new Set())}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        </div>
      )}

      {/* Work Items Table (desktop) / Cards (mobile) */}
      <Card>
        <CardContent className="p-0">
          {filteredItems.length > 0 ? (
            <>
            {/* Desktop table */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="bg-muted/50">
                  {!isReader && (
                    <TableHead className="w-[40px]">
                      <Checkbox
                        checked={filteredItems.length > 0 && selectedIds.size === filteredItems.length}
                        onCheckedChange={toggleSelectAll}
                        aria-label="Tout sélectionner"
                      />
                    </TableHead>
                  )}
                  <TableHead className="font-semibold">ID</TableHead>
                  <TableHead className="font-semibold">Titre</TableHead>
                  <TableHead className="font-semibold">Type</TableHead>
                  <TableHead className="font-semibold">État</TableHead>
                  <TableHead className="font-semibold text-right">Assigné à</TableHead>
                  <TableHead className="font-semibold">Tags</TableHead>
                  <TableHead className="font-semibold">Projet</TableHead>
                  <TableHead className="font-semibold text-right">Heures</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredItems.map((item) => (
                  <TableRow
                    key={item.id}
                    className={`table-row-hover ${selectedIds.has(item.id) ? 'bg-primary/5' : ''}`}
                    data-testid={`work-item-row-${item.id}`}
                  >
                    {!isReader && (
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Checkbox
                          checked={selectedIds.has(item.id)}
                          onCheckedChange={() => toggleSelectItem(item.id)}
                          aria-label={`Sélectionner ${item.title}`}
                        />
                      </TableCell>
                    )}
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {item.azure_id || item.id}
                    </TableCell>
                    <TableCell className="font-medium max-w-[300px] truncate">
                      {item.title}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className={getWorkItemTypeClass(item.work_item_type)}>
                        {item.work_item_type}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="secondary" className={getStateClass(item.state)}>
                        {item.state}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      {item.assigned_to && (
                        <div className="text-xs text-muted-foreground truncate max-w-[120px]">
                          {item.assigned_to}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {(item.tags || []).map((tag, idx) => (
                          <Badge key={idx} variant="outline" className="text-[10px] py-0 px-1.5">
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell>
                      {item.project_name && (
                        <Badge variant="outline" className="text-[10px] py-0">
                          {item.project_name}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right text-sm tabular-nums">
                      {item.completed_hours > 0 ? `${item.completed_hours}h` : '—'}
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          {!isReader && <DropdownMenuItem onClick={() => openEditDialog(item)}>
                            <Pencil className="h-4 w-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>}
                          {!isReader && <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => openDeleteDialog(item)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>}
                          <DropdownMenuItem onClick={() => window.open(item.url, '_blank')}>
                            <Eye className="h-4 w-4 mr-2" />
                            Ouvrir dans Azure DevOps
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>

            {/* Mobile cards */}
            <div className="md:hidden divide-y">
              {filteredItems.map((item) => (
                <div
                  key={item.id}
                  className={`p-4 space-y-3 ${selectedIds.has(item.id) ? 'bg-primary/5' : ''}`}
                  data-testid={`work-item-card-${item.id}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    {!isReader && (
                      <Checkbox
                        checked={selectedIds.has(item.id)}
                        onCheckedChange={() => toggleSelectItem(item.id)}
                        className="mt-0.5 shrink-0"
                        aria-label={`Sélectionner ${item.title}`}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-sm leading-tight line-clamp-2">{item.title}</p>
                      <span className="font-mono text-[10px] text-muted-foreground">#{item.azure_id || item.id}</span>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 shrink-0">
                          <MoreVertical className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {!isReader && <DropdownMenuItem onClick={() => openEditDialog(item)}>
                          <Pencil className="h-4 w-4 mr-2" />
                          Modifier
                        </DropdownMenuItem>}
                        {!isReader && <DropdownMenuItem
                          className="text-destructive focus:text-destructive"
                          onClick={() => openDeleteDialog(item)}
                        >
                          <Trash2 className="h-4 w-4 mr-2" />
                          Supprimer
                        </DropdownMenuItem>}
                        <DropdownMenuItem onClick={() => window.open(item.url, '_blank')}>
                          <Eye className="h-4 w-4 mr-2" />
                          Ouvrir dans Azure DevOps
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>

                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className={`text-[10px] ${getWorkItemTypeClass(item.work_item_type)}`}>
                      {item.work_item_type}
                    </Badge>
                    <Badge variant="secondary" className={`text-[10px] ${getStateClass(item.state)}`}>
                      {item.state}
                    </Badge>
                    {item.project_name && (
                      <Badge variant="outline" className="text-[10px] py-0">
                        {item.project_name}
                      </Badge>
                    )}
                    {(item.tags || []).map((tag, idx) => (
                      <Badge key={idx} variant="outline" className="text-[10px] py-0 px-1.5">
                        {tag}
                      </Badge>
                    ))}
                    {item.completed_hours > 0 && (
                      <Badge variant="secondary" className="text-[10px] py-0">
                        {item.completed_hours}h
                      </Badge>
                    )}
                  </div>

                  {item.assigned_to && (
                    <p className="text-xs text-muted-foreground truncate">
                      Assigné à : {item.assigned_to}
                    </p>
                  )}
                </div>
              ))}
            </div>
            </>
          ) : (
            <div className="empty-state py-16">
              <Filter className="empty-state-icon" />
              <p className="empty-state-title">Aucune tâche Azure DevOps</p>
              <p className="empty-state-description">
                Créez une tâche ou vérifiez vos projets Azure DevOps assignés
              </p>
              {!isReader && <Button className="mt-4 rounded-sm" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Créer une tâche
              </Button>}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Modifier la Tâche #{editItem?.azure_id}</DialogTitle>
            <DialogDescription>
              Les modifications seront appliquées directement sur Azure DevOps
            </DialogDescription>
          </DialogHeader>
          {editItem && (
            <form onSubmit={handleEditItem}>
              <div className="grid grid-cols-2 gap-4 py-4">
                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-title">Titre *</Label>
                  <Input
                    id="edit-title"
                    value={editItem.title}
                    onChange={(e) => setEditItem({ ...editItem, title: e.target.value })}
                    placeholder="Titre de la tâche"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label>État</Label>
                  <Select
                    value={editItem.state}
                    onValueChange={(v) => setEditItem({ ...editItem, state: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATES.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-assigned">Assigné à</Label>
                  <Input
                    id="edit-assigned"
                    value={editItem.assigned_to}
                    onChange={(e) => setEditItem({ ...editItem, assigned_to: e.target.value })}
                    placeholder="nom@email.com"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit-area">Area Path</Label>
                  <Input
                    id="edit-area"
                    value={editItem.area_path}
                    onChange={(e) => setEditItem({ ...editItem, area_path: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Sprint</Label>
                  <Select
                    value={
                      !editItem.iteration_path ||
                      editItem.iteration_path === editItem.project_name
                        ? "project_root"
                        : editItem.iteration_path
                    }
                    onValueChange={(v) => setEditItem({
                      ...editItem,
                      iteration_path: v === "project_root" ? editItem.project_name : v
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="project_root">
                        {editItem.project_name} (sans sprint)
                      </SelectItem>
                      {editIterations.map(it => (
                        <SelectItem key={it.id} value={it.path}>{it.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {editItem.work_item_type === 'Task' && (
                <div className="space-y-2">
                  <Label htmlFor="edit-completed-hours">Heures complétées</Label>
                  <Input
                    id="edit-completed-hours"
                    type="number"
                    min="0"
                    step="0.5"
                    value={editItem.completed_hours || 0}
                    onChange={(e) => setEditItem({ ...editItem, completed_hours: parseFloat(e.target.value) || 0 })}
                  />
                </div>
                )}

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-tags">Tags</Label>
                  <Input
                    id="edit-tags"
                    value={editItem.tags}
                    onChange={(e) => setEditItem({ ...editItem, tags: e.target.value })}
                    placeholder="Tag1; Tag2; Tag3"
                  />
                  <p className="text-xs text-muted-foreground">Séparez les tags par des points-virgules (;)</p>
                </div>

                <div className="col-span-2 space-y-2">
                  <Label htmlFor="edit-description">Description</Label>
                  <Textarea
                    id="edit-description"
                    value={editItem.description}
                    onChange={(e) => setEditItem({ ...editItem, description: e.target.value })}
                    placeholder="Description de la tâche..."
                    rows={4}
                  />
                </div>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                  Annuler
                </Button>
                <Button type="submit" disabled={updating}>
                  {updating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Enregistrer'}
                </Button>
              </DialogFooter>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Edit Dialog */}
      <Dialog open={bulkEditDialogOpen} onOpenChange={setBulkEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Modifier {selectedIds.size} tâche{selectedIds.size > 1 ? 's' : ''}</DialogTitle>
            <DialogDescription>
              Seuls les champs renseignés seront modifiés. Les autres restent inchangés.
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 py-4">
            <div className="space-y-2">
              <Label>État</Label>
              <Select
                value={bulkEditData.state || 'unchanged'}
                onValueChange={(v) => setBulkEditData({ ...bulkEditData, state: v === 'unchanged' ? '' : v })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="— ne pas modifier —" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="unchanged">— ne pas modifier —</SelectItem>
                  {STATES.map(s => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="bulk-assigned">Assigné à</Label>
              <Input
                id="bulk-assigned"
                value={bulkEditData.assigned_to}
                onChange={(e) => setBulkEditData({ ...bulkEditData, assigned_to: e.target.value })}
                placeholder="— ne pas modifier —"
              />
            </div>

            <div className="col-span-2 space-y-2">
              <Label htmlFor="bulk-tags">Tags</Label>
              <Input
                id="bulk-tags"
                value={bulkEditData.tags}
                onChange={(e) => setBulkEditData({ ...bulkEditData, tags: e.target.value })}
                placeholder="— ne pas modifier —"
              />
              <p className="text-xs text-muted-foreground">Séparez les tags par des points-virgules (;). Remplace les tags existants.</p>
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setBulkEditDialogOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleBulkEdit} disabled={bulkProcessing || (!bulkEditData.state && !bulkEditData.assigned_to && !bulkEditData.tags)}>
              {bulkProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Appliquer à ${selectedIds.size} tâche${selectedIds.size > 1 ? 's' : ''}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteDialogOpen} onOpenChange={setBulkDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer {selectedIds.size} tâche{selectedIds.size > 1 ? 's' : ''} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. Les {selectedIds.size} tâches sélectionnées seront définitivement supprimées d'Azure DevOps.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={bulkProcessing}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkDelete}
              disabled={bulkProcessing}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {bulkProcessing ? <Loader2 className="h-4 w-4 animate-spin" /> : `Supprimer ${selectedIds.size} tâche${selectedIds.size > 1 ? 's' : ''}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer la tâche #{deleteItem?.azure_id} ?</AlertDialogTitle>
            <AlertDialogDescription>
              Cette action est irréversible. La tâche sera définitivement supprimée
              d'Azure DevOps et ne pourra pas être récupéré.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={deleting}>Annuler</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteItem}
              disabled={deleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Supprimer'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
